import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, MapPin } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const when = (s) => `${new Date(`${s.date}T12:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · ${s.start}–${s.end}`;

const SlotLine = ({ s, action }) => (
  <div data-testid={`rider-slot-${s.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
    <div className="min-w-0 text-sm">
      <div className="font-semibold">{when(s)}</div>
      <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {s.location} · with {s.assessor_name}</div>
      {s.notes && <div className="text-xs text-muted-foreground">{s.notes}</div>}
    </div>
    {action}
  </div>
);

export const BookReassessment = ({ status }) => {
  const [slots, setSlots] = useState(null);
  const load = () => api.get("/assessment-slots").then((r) => setSlots(r.data)).catch(() => setSlots([]));
  useEffect(() => { load(); }, []);
  const eligible = ["due_soon", "overdue"].includes(status);
  const mine = slots?.find((s) => s.status === "booked");
  const cancelled = mine ? [] : slots?.filter((s) => s.status === "cancelled") || [];
  const open = slots?.filter((s) => s.status === "open") || [];
  if (!eligible && !mine) return null;
  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); }
    load();
  };
  return (
    <section data-testid="book-reassessment" className="overflow-hidden rounded-2xl border-2 border-primary/50 bg-card">
      <div className="flex items-center gap-2 border-b px-5 py-4 font-bold"><CalendarClock className="h-5 w-5 text-primary" /> Book your re-assessment</div>
      {cancelled.map((s) => <p key={s.id} data-testid={`slot-cancelled-${s.id}`} className="border-b bg-red-500/10 px-5 py-2 text-xs text-red-300">Your slot on {when(s)} was cancelled by {s.cancelled_by}. Please choose another.</p>)}
      {mine ? (
        <div data-testid="my-booked-slot" className="bg-emerald-500/10">
          <p className="px-5 pt-3 text-xs font-bold uppercase tracking-wide text-emerald-300">You're booked</p>
          <SlotLine s={mine} action={<button onClick={() => window.confirm("Cancel this booking?") && act(() => api.post(`/assessment-slots/${mine.id}/release`), "Booking cancelled")} data-testid="cancel-my-slot-btn" className="rounded-full border px-4 py-1.5 text-xs font-semibold">Cancel booking</button>} />
        </div>
      ) : slots === null ? <div className="h-16 animate-pulse" /> : open.length === 0 ? (
        <p data-testid="no-slots-msg" className="px-5 py-6 text-sm text-muted-foreground">No slots available yet. Assessors post new dates here, so check back soon or contact the Lead Assessor.</p>
      ) : (
        <div className="divide-y">
          {open.map((s) => <SlotLine key={s.id} s={s} action={<button onClick={() => act(() => api.post(`/assessment-slots/${s.id}/book`), "Re-assessment booked")} data-testid={`book-slot-${s.id}`} className="rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground">Book</button>} />)}
        </div>
      )}
    </section>
  );
};
